import { useCallback } from "react";
import {
  useLoaderData,
  useNavigation,
  useSearchParams,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { ActiveFilterChips } from "@/components/gsc/ActiveFilterChips";
import { DetailTable } from "@/components/gsc/DetailTable";
import { GscFiltersPanel } from "@/components/gsc/GscFiltersPanel";
import { GscSubNav } from "@/components/gsc/GscSubNav";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { queryClient } from "@/lib/queryClient";
import {
  getUrlFilterMultiValues,
  isUrlSlugFilterKey,
  parseUrlActiveFilters,
  serializeUrlActiveFilters,
} from "@/lib/url-active-filters";
import {
  EMPTY_GSC_DETAIL_RESULT,
  fetchGscDetail,
  fetchGscFilterOptions,
  getGscDetailQueryKey,
  GSC_FILTER_OPTIONS_QUERY_KEY,
  GSC_FILTER_OPTIONS_STALE_TIME,
  GSC_STATS_STALE_TIME,
  parseGscDetailParamsFromUrl,
  useGscDetail,
  useGscFilterOptions,
  type GscDetailParams,
} from "@/hooks/useGscData";
import {
  EMPTY_GSC_FILTER_OPTIONS,
  GSC_DEFAULT_PAGE_SIZE,
  type GscDetailSortColumn,
  type GscFilterKey,
  type GscPageSize,
} from "@/types/gsc";

export type GscDetailLoaderData = GscDetailParams;

export async function gscDetailLoader({
  request,
}: LoaderFunctionArgs): Promise<GscDetailLoaderData> {
  const url = new URL(request.url);
  const params = parseGscDetailParamsFromUrl(url);

  // prefetchQuery does not throw on query failures; still swallow any unexpected
  // rejections so the route always mounts and useGscDetail can surface isError.
  await Promise.all([
    queryClient
      .prefetchQuery({
        queryKey: getGscDetailQueryKey(params),
        queryFn: () => fetchGscDetail(params),
        staleTime: GSC_STATS_STALE_TIME,
      })
      .catch(() => undefined),
    queryClient
      .prefetchQuery({
        queryKey: GSC_FILTER_OPTIONS_QUERY_KEY,
        queryFn: fetchGscFilterOptions,
        staleTime: GSC_FILTER_OPTIONS_STALE_TIME,
      })
      .catch(() => undefined),
  ]);

  return params;
}

export default function GscDetail() {
  const params = useLoaderData() as GscDetailLoaderData;
  const navigation = useNavigation();
  const [, setSearchParams] = useSearchParams();
  const { data: detail, isPending, isFetching, isError } = useGscDetail(params);
  const { data: filterOptions, isPending: isFilterOptionsPending } =
    useGscFilterOptions();

  const isRouteLoading = navigation.state === "loading";
  const hasDetailData = detail !== undefined;
  const isLoading = (isRouteLoading || isPending) && !hasDetailData;
  const isTableFetching = isFetching && hasDetailData;
  const { rows, totalCount } = detail ?? EMPTY_GSC_DETAIL_RESULT;

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) next.delete("page");
        else next.set("page", String(page));
        return next;
      });
    },
    [setSearchParams],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: GscPageSize) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (pageSize === GSC_DEFAULT_PAGE_SIZE) next.delete("pageSize");
        else next.set("pageSize", String(pageSize));
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const handleDrillDown = useCallback(
    (key: GscFilterKey, value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const filters = parseUrlActiveFilters(prev.get("filters"));

        if (isUrlSlugFilterKey(key)) {
          filters[key] = { mode: "include", value: trimmed };
        } else {
          const existing = getUrlFilterMultiValues(filters[key]);
          const values = existing.includes(trimmed)
            ? existing
            : [...existing, trimmed];
          filters[key] = { mode: "include", values };
        }

        const serialized = serializeUrlActiveFilters(filters);
        if (serialized) next.set("filters", serialized);
        else next.delete("filters");
        next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSortChange = useCallback(
    (column: GscDetailSortColumn) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentSort = params.sortBy;
        const currentDir = params.sortDir;

        if (column === currentSort) {
          const nextDir = currentDir === "asc" ? "desc" : "asc";
          if (column === "clicks" && nextDir === "desc") {
            next.delete("sortBy");
            next.delete("sortDir");
          } else {
            if (column === "clicks") next.delete("sortBy");
            else next.set("sortBy", column);
            if (nextDir === "desc") next.delete("sortDir");
            else next.set("sortDir", nextDir);
          }
        } else {
          const nextDir =
            column === "domain" ||
            column === "clean_url" ||
            column === "keyword" ||
            column === "country" ||
            column === "device"
              ? "asc"
              : "desc";
          if (column === "clicks") next.delete("sortBy");
          else next.set("sortBy", column);
          if (nextDir === "desc") next.delete("sortDir");
          else next.set("sortDir", nextDir);
        }

        next.delete("page");
        return next;
      });
    },
    [params.sortBy, params.sortDir, setSearchParams],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">Detail Table</h1>
        <p className="text-sm text-muted-foreground">
          Full row-level data with drill-down by domain, URL, query, country,
          device.
        </p>
      </div>

      <GscSubNav />

      <div>
        <GscFiltersPanel
          options={filterOptions ?? EMPTY_GSC_FILTER_OPTIONS}
          isLoading={isFilterOptionsPending}
        />
        <ActiveFilterChips />
      </div>

      {isError ? (
        <Card className="border-border shadow-none">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Failed to load GSC detail</AlertTitle>
              <AlertDescription>
                Please try again. If the problem persists, check your connection
                or refresh the page.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <DetailTable
          rows={rows}
          totalCount={totalCount}
          page={params.page}
          pageSize={params.pageSize}
          sortBy={params.sortBy}
          sortDir={params.sortDir}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          onDrillDown={handleDrillDown}
          isLoading={isLoading}
          isFetching={isTableFetching}
        />
      )}
    </div>
  );
}
